import { updateTransform } from "~/solidJs/widgets/WorkspaceOverlayWidget/WorkspaceOverlayWidget";
import Editor from "../editor";

export default function Navigation(editorSpaceElement, graphElement) {
	let willChangeTimeout = null;

	const setWillChange = () => {
		graphElement.style.willChange = "transform";

		if (willChangeTimeout) {
			clearTimeout(willChangeTimeout);
		}

		willChangeTimeout = setTimeout(() => {
			graphElement.style.willChange = "";
		}, 250);
	};

	const setTransform = (left, top, zoomScale) => {
		setWillChange();

		// matrix3d(scaleX, 0, 0, 0, 0, scaleY, 0, 0, 0, 0, scaleZ, 0, translateX, translateY, translateZ, 1)
		graphElement.style.transform = `matrix3d(${zoomScale}, 0, 0, 0, 0, ${zoomScale}, 0, 0, 0, 0, 1, 0, ${left}, ${top}, 0, 1)`;

		const shiftX = (spaceSize.x / 2) * (1 - zoomScale);
		const shiftY = (spaceSize.y / 2) * (1 - zoomScale);
		editorSpaceElement.style.backgroundPosition = `${left + shiftX}px ${top + shiftY}px`;
		updateTransform({
			left: left / zoomScale,
			top: top / zoomScale,
			zoom: zoomScale
		});
	};

	this.dragWidthPadding = window.innerWidth * 0.1;
	this.dragHeightPadding = window.innerHeight * 0.1;

	// Drag detection for preventing button clicks during drag operations
	const dragDetection = {
		isDragging: false,
		startX: 0,
		startY: 0,
		threshold: 10
	};

	const resetDragState = () => {
		dragDetection.isDragging = false;
		dragDetection.startX = 0;
		dragDetection.startY = 0;
	};

	const initializeDragDetection = () => {
		// Track mousedown to start drag detection (capture phase)
		editorSpaceElement.addEventListener(
			"mousedown",
			(e) => {
				dragDetection.startX = e.clientX;
				dragDetection.startY = e.clientY;
				dragDetection.isDragging = false;
			},
			true
		);

		// Track mousemove to detect dragging (capture phase)
		editorSpaceElement.addEventListener(
			"mousemove",
			(e) => {
				if (dragDetection.startX === 0 && dragDetection.startY === 0) return;

				const deltaX = Math.abs(e.clientX - dragDetection.startX);
				const deltaY = Math.abs(e.clientY - dragDetection.startY);

				if (deltaX > dragDetection.threshold || deltaY > dragDetection.threshold) {
					dragDetection.isDragging = true;
				}
			},
			true
		);

		// Prevent clicks on buttons during drag operations (capture phase)
		editorSpaceElement.addEventListener(
			"click",
			(e) => {
				if (dragDetection.isDragging) {
					e.preventDefault();
					e.stopPropagation();
					e.stopImmediatePropagation();
				}
				resetDragState();
			},
			true
		);
	};

	const spaceSize = {
		x: editorSpaceElement.offsetWidth,
		y: editorSpaceElement.offsetHeight
	};

	let zoomValue = document.getElementById("zoom-value");

	let zoomScale = 0.8;
	const position = { x: 0, y: 0 };
	let prevFontSize;
	/**
	 * @description dynamycly change font size of nodes
	 * @param {*} zoomPercentage
	 */
	const _changeFontValue = (zoomPercentage) => {
		if (zoomPercentage < 50) {
			if (prevFontSize === 50) return;
			prevFontSize = 50;
			graphElement.classList.add("font-50");

			return;
		}
		if (prevFontSize === 100) return;
		prevFontSize = 100;
		graphElement.classList.remove("font-50");
	};
	this.getZoomScale = () => zoomScale;
	this.getPosition = () => ({
		x: Number(position.x),
		y: Number(position.y)
	});
	this.getSize = () => spaceSize;

	this.clientToGraphSpace = (clientX, clientY) => {
		const graphWidth = this.getGraphWidth();
		const graphHeight = this.getGraphHeight();

		const centerX = position.x + graphWidth / 2;
		const centerY = position.y + graphHeight / 2;

		const distX = clientX - centerX;
		const distY = clientY - centerY;

		const offsetX = distX / zoomScale;
		const offsetY = distY / zoomScale;

		return {
			x: Math.round(graphWidth / 2 + offsetX),
			y: Math.round(graphHeight / 2 + offsetY)
		};
	};
	this.getGraphWidth = () => {
		return graphElement.offsetWidth;
	};
	this.getGraphHeight = () => {
		return graphElement.offsetHeight;
	};
	this.updatePositionBy = (x, y) => {
		const { x: oldX, y: oldY } = this.getPosition();
		const updatedX = oldX + x;
		const updatedY = oldY + y;
		this.setPosition(updatedX, updatedY);
	};

	const pressedKeyCodes = {};
	window.addEventListener("keydown", (e) => {
		pressedKeyCodes[e.code] = true;
	});
	window.addEventListener("keyup", (e) => {
		pressedKeyCodes[e.code] = false;
	});

	this.setPosition = (x, y) => {
		setTransform(x, y, zoomScale);
		position.x = x;
		position.y = y;
		Editor.context.graph.isLastNodeWasMoved = true;
	};

	this.restorePosition = (prevGraph) => {
		const pos = prevGraph.navigation.getPosition();
		const zoom = prevGraph.navigation.getZoomScale();
		this.setPosition(pos.x, pos.y);
		this.setZoomScale(zoom);
	};

	const panByDrag = (mouseEvent) => {
		if (pressedKeyCodes.Space) {
			document.body.style.cursor = "grab";
		} else {
			document.body.style.cursor = "default";
		}
		if ((pressedKeyCodes.Space && mouseEvent.which === 1) || mouseEvent.which === 2) {
			document.body.style.cursor = "grabbing";
			mouseEvent.stopPropagation();
			this.setPosition(position.x + mouseEvent.movementX, position.y + mouseEvent.movementY);
		}
	};

	const panByScroll = (mouseEvent) => {
		if (mouseEvent.ctrlKey || mouseEvent.metaKey) return;
		mouseEvent.preventDefault();
		mouseEvent.stopPropagation();
		this.setPosition(position.x - mouseEvent.deltaX, position.y - mouseEvent.deltaY);
	};
	const zoomMac = (mouseEvent) => {
		if (!(mouseEvent.ctrlKey || mouseEvent.metaKey)) return;
		mouseEvent.preventDefault();

		const previousScale = zoomScale;
		this.setZoomScale(zoomScale - Math.min(mouseEvent.deltaY * 0.025, 0.5));

		const pos = this.getPosition();
		// Zoom to cursor
		const centerX = pos.x + graphElement.offsetWidth / 2;
		const centerY = pos.y + graphElement.offsetHeight / 2;

		const pivotX = mouseEvent.clientX;
		const pivotY = mouseEvent.clientY;

		const distX = pivotX - centerX;
		const distY = pivotY - centerY;

		const scale = 1 - zoomScale / previousScale;
		const offsetX = distX * scale;
		const offsetY = distY * scale;

		this.setPosition(position.x + offsetX, position.y + offsetY);
	};
	const zoomWindows = (mouseEvent) => {
		if (!(mouseEvent.ctrlKey || mouseEvent.metaKey)) return;
		mouseEvent.preventDefault();
		const previousScale = zoomScale;

		this.setZoomScale(zoomScale - Math.min(mouseEvent.deltaY * 0.002, 0.5));

		const pos = this.getPosition();
		// Zoom to cursor
		const centerX = pos.x + graphElement.offsetWidth / 2;
		const centerY = pos.y + graphElement.offsetHeight / 2;

		const pivotX = mouseEvent.clientX;
		const pivotY = mouseEvent.clientY;

		const distX = pivotX - centerX;
		const distY = pivotY - centerY;

		const scale = 1 - zoomScale / previousScale;
		const offsetX = distX * scale;
		const offsetY = distY * scale;

		this.setPosition(position.x + offsetX, position.y + offsetY);
	};

	this.setZoomScale = (value) => {
		Editor.context.graph.isLastNodeWasMoved = true;
		const validatedZoomScale = Math.min(Math.max(0.125, value), 3);
		zoomScale = validatedZoomScale;
		const pos = this.getPosition();
		setTransform(pos.x, pos.y, zoomScale);
		if (Editor.context.graph.nodes.length < 40) {
			editorSpaceElement.style.backgroundSize = `${Math.max(
				validatedZoomScale * 1.25,
				0.5
			)}%`;
		}
		if (this.timeout) clearTimeout(this.timeout);
		const percentageValue = ((validatedZoomScale - 1) / 1.875) * 175 + 100;

		this.timeout = setTimeout(() => {
			zoomValue = zoomValue || document.getElementById("$zoom-value");
			if (!zoomValue) return;
			zoomValue.value = `${Math.round(percentageValue)}%`;
		}, 50);
	};
	this.moveGraphByDrag = (pageX, pageY) => {
		let offsetX = 0;
		let offsetY = 0;
		let speedFactorX = 1;
		let speedFactorY = 1;

		if (pageX > window.innerWidth - this.dragWidthPadding) {
			speedFactorX = pageX / (window.innerWidth - this.dragWidthPadding);
			speedFactorX = speedFactorX ** 6 * 2;
			offsetX = -1 * speedFactorX;
		}
		if (pageX < this.dragWidthPadding) {
			speedFactorX = this.dragWidthPadding / (pageX + 50);
			offsetX = 1 * speedFactorX;
		}
		if (pageY > window.innerHeight - this.dragHeightPadding) {
			speedFactorY = pageY / (window.innerHeight - this.dragHeightPadding);
			speedFactorY = speedFactorY ** 4 * 2;
			offsetY = -1 * speedFactorY;
		}
		if (pageY < this.dragHeightPadding) {
			speedFactorY = this.dragHeightPadding / (pageY + 100);
			offsetY = 1 * speedFactorY;
		}

		this.updatePositionBy(offsetX, offsetY);
	};

	// Initialize with matrix3d
	graphElement.style.transform = `matrix3d(${zoomScale}, 0, 0, 0, 0, ${zoomScale}, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)`;

	editorSpaceElement.addEventListener(
		"wheel",
		navigator.userAgent.indexOf("Mac") !== -1 ? zoomMac : zoomWindows,
		false
	);
	editorSpaceElement.addEventListener("wheel", panByScroll, false);
	editorSpaceElement.addEventListener("mousemove", panByDrag, false);

	// Initialize drag detection to prevent button clicks during drag operations
	initializeDragDetection();
}
